import { Link } from 'react-router-dom';
import type { MangaItem } from '@/mocks/manga';

interface MangaCardProps {
  manga: MangaItem;
  variant?: 'default' | 'compact' | 'featured';
}

export default function MangaCard({ manga, variant = 'default' }: MangaCardProps) {
  const formatViews = (views: number) => {
    if (views >= 10000) {
      return `${(views / 10000).toFixed(1)}万`;
    }
    return views.toString();
  };

  if (variant === 'compact') {
    return (
      <Link
        to={`/manga/${manga.slug}`}
        className="group block cursor-pointer"
        data-product-shop
      >
        <div className="relative overflow-hidden rounded-lg aspect-[3/4] bg-background-200">
          <img
            src={manga.cover}
            alt={manga.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute top-2 left-2">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
              manga.status === '连载中'
                ? 'bg-primary-500 text-background-50'
                : 'bg-secondary-500 text-background-50'
            }`}>
              {manga.status}
            </span>
          </div>
          <div className="absolute bottom-2 right-2">
            <span className="px-1.5 py-0.5 rounded bg-background-950/60 text-background-50 text-xs whitespace-nowrap">
              ★ {manga.rating}
            </span>
          </div>
        </div>
        <h3 className="mt-2 text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-1">
          {manga.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-0.5">{manga.author}</p>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/manga/${manga.slug}`}
        className="group flex gap-3 sm:gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
        data-product-shop
      >
        <div className="relative overflow-hidden rounded-lg w-20 h-28 sm:w-24 sm:h-32 flex-shrink-0 bg-background-200">
          <img
            src={manga.cover}
            alt={manga.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-1">
            {manga.title}
          </h3>
          <p className="text-xs text-foreground-500 mt-1">{manga.author}</p>
          <p className="text-xs text-foreground-600 mt-2 line-clamp-2 leading-relaxed">{manga.description}</p>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
              manga.status === '连载中'
                ? 'bg-primary-100 text-primary-700'
                : 'bg-secondary-100 text-secondary-700'
            }`}>
              {manga.status}
            </span>
            <span className="text-xs text-foreground-500">
              <i className="ri-eye-line mr-0.5"></i>{formatViews(manga.views)}
            </span>
            <span className="text-xs text-accent-500">
              ★ {manga.rating}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/manga/${manga.slug}`}
      className="group block cursor-pointer"
      data-product-shop
    >
      <div className="relative overflow-hidden rounded-lg aspect-[3/4] bg-background-200">
        <img
          src={manga.cover}
          alt={manga.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
          <p className="text-background-50 text-xs leading-relaxed line-clamp-3">{manga.description}</p>
        </div>
        <div className="absolute top-2 left-2">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
            manga.status === '连载中'
              ? 'bg-primary-500 text-background-50'
              : 'bg-secondary-500 text-background-50'
          }`}>
            {manga.status}
          </span>
        </div>
      </div>
      <h3 className="mt-2.5 text-sm font-semibold text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-1">
        {manga.title}
      </h3>
      <div className="flex items-center justify-between mt-1">
        <p className="text-xs text-foreground-500">{manga.author}</p>
        <div className="flex items-center gap-1">
          <span className="text-xs text-accent-500 font-medium">★ {manga.rating}</span>
        </div>
      </div>
    </Link>
  );
}